import type { CommandResult, MediaAsset, RecordingDraft, Transcript, UploadSession, UUID } from '../../domain/types';
import { nowISO } from '../../lib/time';
import { uuid } from '../../lib/uuid';
import type { MediaRepository } from '../types';
import { actorOf, invalid, notFound, okResult } from './actor';
import { chunkKey, deleteBlob, getBlob, mediaKey, putBlob } from './blobStore';
import { MockDb } from './MockDb';

/**
 * Local-first recording and a simulated resumable upload pipeline
 * (section 13). Chunks are written to IndexedDB as they arrive; the asset is
 * only marked "Safely backed up" after the simulated server confirms the
 * upload, commits metadata and completes the independent backup.
 */
export class MockMediaRepository implements MediaRepository {
  private tickers = new Map<UUID, ReturnType<typeof setInterval>>();
  private retryTimers = new Map<UUID, ReturnType<typeof setTimeout>>();
  private urls = new Map<UUID, string>();

  constructor(private db: MockDb) {
    db.onSim(() => {
      // Coming back online resumes paused uploads automatically.
      if (!db.isOffline()) void this.resumePendingUploads();
    });
    if (typeof window !== 'undefined') window.addEventListener('online', () => void this.resumePendingUploads());
  }

  // ---- drafts ------------------------------------------------------------

  async createDraft(input: Omit<RecordingDraft, 'id' | 'createdAt' | 'updatedAt' | 'durationMs' | 'chunkCount' | 'step'> & { id?: UUID }): Promise<RecordingDraft> {
    await this.db.ready;
    const now = nowISO();
    const draft: RecordingDraft = { ...input, id: input.id ?? uuid(), durationMs: 0, chunkCount: 0, step: 0, createdAt: now, updatedAt: now };
    this.db.t.recording_drafts.push(draft);
    await this.db.commit(['recording_drafts']);
    return draft;
  }

  async getDraft(id: UUID): Promise<RecordingDraft | undefined> {
    await this.db.ready;
    return this.db.t.recording_drafts.find((d) => d.id === id);
  }

  async listDrafts(accountId: UUID): Promise<RecordingDraft[]> {
    await this.db.ready;
    return this.db.t.recording_drafts.filter((d) => d.accountId === accountId).sort((a, b) => b.updatedAt.localeCompare(a.updatedAt));
  }

  async updateDraft(id: UUID, patch: Partial<RecordingDraft>): Promise<RecordingDraft | undefined> {
    await this.db.ready;
    const d = this.db.t.recording_drafts.find((x) => x.id === id);
    if (!d) return undefined;
    Object.assign(d, patch, { updatedAt: nowISO() });
    await this.db.commit(['recording_drafts']);
    return d;
  }

  async deleteDraft(id: UUID): Promise<void> {
    await this.db.ready;
    await this.discardRecording(id);
    this.db.t.recording_drafts = this.db.t.recording_drafts.filter((d) => d.id !== id);
    await this.db.commit(['recording_drafts']);
  }

  // ---- local recording ---------------------------------------------------

  async appendChunk(draftId: UUID, index: number, chunk: Blob, durationMsSoFar: number): Promise<void> {
    await putBlob(chunkKey(draftId, index), chunk);
    const d = this.db.t.recording_drafts.find((x) => x.id === draftId);
    if (d) {
      d.chunkCount = Math.max(d.chunkCount, index + 1);
      d.durationMs = durationMsSoFar;
      d.updatedAt = nowISO();
      if (!d.mimeType && chunk.type) d.mimeType = chunk.type;
      await this.db.commit(['recording_drafts']);
    }
  }

  async finalizeLocalRecording(draftId: UUID): Promise<CommandResult<MediaAsset>> {
    await this.db.ready;
    const actor = actorOf(this.db);
    const d = this.db.t.recording_drafts.find((x) => x.id === draftId);
    if (!d) return notFound();
    if (d.mediaAssetId) {
      const existing = this.db.t.media_assets.find((m) => m.id === d.mediaAssetId);
      if (existing) return okResult(existing, { noop: true });
    }
    const parts: Blob[] = [];
    for (let i = 0; i < d.chunkCount; i++) {
      const b = await getBlob(chunkKey(draftId, i));
      if (b) parts.push(b);
    }
    if (!parts.length) return invalid('record.error.empty');
    const blob = new Blob(parts, { type: d.mimeType || parts[0].type || 'audio/webm' });
    const id = uuid();
    await putBlob(mediaKey(id), blob);
    const now = nowISO();
    const asset: MediaAsset = {
      id, familyId: actor.session.familyId, kind: 'audio', role: 'original', mimeType: blob.type, durationMs: d.durationMs, sizeBytes: blob.size, storageKey: mediaKey(id),
      language: 'unknown', speakerPersonId: d.speakerPersonId, uploaderAccountId: actor.session.accountId, status: 'local', backupState: 'none', createdAt: now, updatedAt: now,
    };
    this.db.t.media_assets.push(asset);
    d.mediaAssetId = id;
    d.updatedAt = now;
    for (let i = 0; i < d.chunkCount; i++) await deleteBlob(chunkKey(draftId, i));
    await this.db.commit(['media_assets', 'recording_drafts']);
    return okResult(asset);
  }

  async discardRecording(draftId: UUID): Promise<void> {
    const d = this.db.t.recording_drafts.find((x) => x.id === draftId);
    if (!d) return;
    for (let i = 0; i < d.chunkCount; i++) await deleteBlob(chunkKey(draftId, i));
    if (d.mediaAssetId) {
      const used = this.db.t.story_contributions.some((c) => c.mediaAssetId === d.mediaAssetId);
      if (!used) {
        await deleteBlob(mediaKey(d.mediaAssetId));
        this.db.t.media_assets = this.db.t.media_assets.filter((m) => m.id !== d.mediaAssetId);
        this.db.t.upload_sessions = this.db.t.upload_sessions.filter((u) => u.mediaAssetId !== d.mediaAssetId);
      }
    }
    d.chunkCount = 0;
    d.durationMs = 0;
    d.mediaAssetId = undefined;
    d.updatedAt = nowISO();
    await this.db.commit(['recording_drafts', 'media_assets', 'upload_sessions']);
  }

  // ---- media -------------------------------------------------------------

  async getMedia(id: UUID): Promise<MediaAsset | undefined> {
    await this.db.ready;
    return this.db.t.media_assets.find((m) => m.id === id);
  }

  async listMedia(): Promise<MediaAsset[]> {
    await this.db.ready;
    return [...this.db.t.media_assets];
  }

  async getPlaybackUrl(mediaId: UUID): Promise<string | undefined> {
    const cached = this.urls.get(mediaId);
    if (cached) return cached;
    const asset = this.db.t.media_assets.find((m) => m.id === mediaId);
    if (!asset) return undefined;
    const blob = await getBlob(asset.storageKey);
    if (!blob) return undefined;
    const url = URL.createObjectURL(blob);
    this.urls.set(mediaId, url);
    return url;
  }

  // ---- simulated resumable upload ---------------------------------------

  async startUpload(mediaId: UUID, idempotencyKey: string): Promise<UploadSession> {
    await this.db.ready;
    const asset = this.db.t.media_assets.find((m) => m.id === mediaId);
    if (!asset) throw new Error('media not found');
    let up = this.db.t.upload_sessions.find((u) => u.mediaAssetId === mediaId);
    const now = nowISO();
    if (!up) {
      up = { id: uuid(), mediaAssetId: mediaId, localDraftId: mediaId, idempotencyKey, totalBytes: asset.sizeBytes, bytesUploaded: 0, state: 'uploading', attempts: 0, createdAt: now, updatedAt: now };
      this.db.t.upload_sessions.push(up);
    } else if (up.state === 'complete') {
      return up;
    } else {
      up.state = 'uploading';
      up.updatedAt = now;
    }
    if (asset.status === 'local' || asset.status === 'paused_retry' || asset.status === 'action_needed') {
      asset.status = 'uploading';
      asset.updatedAt = now;
    }
    await this.db.commit(['upload_sessions', 'media_assets'], [mediaId]);
    this.runTicker(mediaId);
    return up;
  }

  private stopTicker(mediaId: UUID): void {
    const t = this.tickers.get(mediaId);
    if (t) clearInterval(t);
    this.tickers.delete(mediaId);
  }

  private scheduleRetry(up: UploadSession): void {
    const existing = this.retryTimers.get(up.mediaAssetId);
    if (existing) clearTimeout(existing);
    const delayMs = Math.min(30_000, 2000 * Math.pow(2, Math.max(0, up.attempts - 1)));
    up.nextRetryAt = new Date(Date.now() + delayMs).toISOString();
    this.retryTimers.set(
      up.mediaAssetId,
      setTimeout(() => {
        this.retryTimers.delete(up.mediaAssetId);
        const cur = this.db.t.upload_sessions.find((u) => u.id === up.id);
        if (cur && cur.state === 'paused' && !this.db.isOffline()) void this.startUpload(up.mediaAssetId, up.idempotencyKey);
      }, delayMs),
    );
  }

  private runTicker(mediaId: UUID): void {
    this.stopTicker(mediaId);
    const tick = async () => {
      const db = this.db;
      const up = db.t.upload_sessions.find((u) => u.mediaAssetId === mediaId);
      const asset = db.t.media_assets.find((m) => m.id === mediaId);
      if (!up || !asset || up.state === 'complete' || up.state === 'action_needed') {
        this.stopTicker(mediaId);
        return;
      }
      const sim = db.getSim();
      const now = nowISO();
      up.updatedAt = now;
      asset.updatedAt = now;
      let transcribed = false;

      const fail = db.isOffline() || (sim.uploadFailures && Math.random() < 0.18);
      if (fail && (up.state === 'uploading' || up.state === 'verifying' || up.state === 'backing_up')) {
        up.attempts += 1;
        up.lastError = db.isOffline() ? 'No connection' : 'Connection dropped during transfer';
        if (up.attempts >= 4 && !db.isOffline()) {
          up.state = 'action_needed';
          asset.status = 'action_needed';
          this.stopTicker(mediaId);
          db.notify({ accountId: asset.uploaderAccountId, kind: 'upload', title: 'A recording could not be uploaded — action needed', titleHe: 'הקלטה לא הצליחה לעלות — נדרשת פעולה', link: '/timeline' });
          await db.commit(['upload_sessions', 'media_assets', 'notifications'], [mediaId]);
          return;
        }
        up.state = 'paused';
        asset.status = 'paused_retry';
        this.stopTicker(mediaId);
        if (!db.isOffline()) this.scheduleRetry(up);
        await db.commit(['upload_sessions', 'media_assets'], [mediaId]);
        return;
      }

      switch (up.state) {
        case 'uploading': {
          const step = Math.max(1, Math.round(up.totalBytes * (sim.slowUploads ? 0.02 : 0.06 + Math.random() * 0.08)));
          up.bytesUploaded = Math.min(up.totalBytes, up.bytesUploaded + step);
          if (up.bytesUploaded >= up.totalBytes) up.state = 'verifying';
          break;
        }
        case 'verifying':
          up.state = 'committing';
          break;
        case 'committing':
          // Server confirmed the complete original + metadata committed (section 13.1)
          asset.status = 'uploaded';
          asset.backupState = 'pending';
          up.state = 'backing_up';
          break;
        case 'backing_up':
          if (Math.random() < 0.5 || sim.slowUploads === false) {
            asset.backupState = 'done';
            asset.status = 'backed_up';
            up.state = 'complete';
            this.stopTicker(mediaId);
            this.queueTranscription(asset);
            transcribed = true;
          }
          break;
        case 'paused':
        case 'idle':
          up.state = 'uploading';
          break;
        default:
          break;
      }
      // Only what this tick touched: the transcripts table is written once, at the end, not every 400ms of every upload.
      await db.commit(transcribed ? ['upload_sessions', 'media_assets', 'transcripts'] : ['upload_sessions', 'media_assets'], [mediaId]);
    };
    this.tickers.set(mediaId, setInterval(() => void tick(), this.db.getSim().slowUploads ? 700 : 400));
  }

  private queueTranscription(asset: MediaAsset): void {
    const contribution = this.db.t.story_contributions.find((c) => c.mediaAssetId === asset.id);
    if (!contribution) return;
    let tr = this.db.t.transcripts.find((t) => t.contributionId === contribution.id);
    if (!tr) {
      tr = { id: uuid(), contributionId: contribution.id, language: 'unknown', text: '', isTranslation: false, source: 'ai', status: 'processing', reviewStatus: 'unreviewed', version: 1, updatedAt: nowISO() };
      this.db.t.transcripts.push(tr);
    } else {
      tr.status = 'processing';
    }
    const id = tr.id;
    setTimeout(() => {
      const t = this.db.t.transcripts.find((x) => x.id === id);
      if (!t) return;
      t.status = 'ready';
      t.language = 'unknown';
      t.text = '';
      t.updatedAt = nowISO();
      void this.db.commit(['transcripts']);
    }, 4000);
  }

  async pauseUpload(mediaId: UUID): Promise<void> {
    const up = this.db.t.upload_sessions.find((u) => u.mediaAssetId === mediaId);
    const asset = this.db.t.media_assets.find((m) => m.id === mediaId);
    if (!up || !asset || up.state === 'complete') return;
    this.stopTicker(mediaId);
    const t = this.retryTimers.get(mediaId);
    if (t) clearTimeout(t);
    up.state = 'paused';
    up.nextRetryAt = undefined;
    asset.status = 'paused_retry';
    await this.db.commit(['upload_sessions', 'media_assets'], [mediaId]);
  }

  async resumeUpload(mediaId: UUID): Promise<void> {
    const up = this.db.t.upload_sessions.find((u) => u.mediaAssetId === mediaId);
    if (!up) return;
    await this.startUpload(mediaId, up.idempotencyKey);
  }

  async retryUpload(mediaId: UUID): Promise<void> {
    const up = this.db.t.upload_sessions.find((u) => u.mediaAssetId === mediaId);
    if (!up) return;
    up.attempts = 0;
    up.lastError = undefined;
    await this.startUpload(mediaId, up.idempotencyKey);
  }

  async getUpload(mediaId: UUID): Promise<UploadSession | undefined> {
    await this.db.ready;
    return this.db.t.upload_sessions.find((u) => u.mediaAssetId === mediaId);
  }

  async listUploads(): Promise<UploadSession[]> {
    await this.db.ready;
    return [...this.db.t.upload_sessions];
  }

  async resumePendingUploads(): Promise<void> {
    await this.db.ready;
    if (this.db.isOffline()) return;
    for (const up of this.db.t.upload_sessions) {
      if (up.state === 'complete' || up.state === 'action_needed') continue;
      if (this.tickers.has(up.mediaAssetId) || this.retryTimers.has(up.mediaAssetId)) continue;
      // Another tab may be driving this upload; only take over if it looks stale.
      const stale = Date.now() - new Date(up.updatedAt).getTime() > 5000;
      if (stale) void this.startUpload(up.mediaAssetId, up.idempotencyKey);
    }
  }

  async listTranscripts(): Promise<Transcript[]> {
    await this.db.ready;
    return [...this.db.t.transcripts];
  }

  async retryTranscription(contributionId: UUID): Promise<void> {
    await this.db.ready;
    const tr = this.db.t.transcripts.find((t) => t.contributionId === contributionId);
    if (!tr) return;
    tr.status = 'processing';
    tr.error = undefined;
    await this.db.commit(['transcripts']);
    setTimeout(() => {
      tr.status = 'ready';
      tr.updatedAt = nowISO();
      if (!tr.text) tr.text = '';
      void this.db.commit(['transcripts']);
    }, 3500);
  }

  async retryBackup(mediaId: UUID): Promise<void> {
    await this.db.ready;
    const asset = this.db.t.media_assets.find((m) => m.id === mediaId);
    if (!asset) return;
    asset.backupState = 'pending';
    await this.db.commit(['media_assets'], [mediaId]);
    setTimeout(() => {
      asset.backupState = 'done';
      if (asset.status === 'uploaded') asset.status = 'backed_up';
      asset.updatedAt = nowISO();
      void this.db.commit(['media_assets'], [mediaId]);
    }, 2500);
  }

  recordingSupport(): { supported: boolean; mimeType: string | null; reason?: 'insecure' | 'no_api' } {
    if (typeof window === 'undefined') return { supported: false, mimeType: null, reason: 'no_api' };
    if (!window.isSecureContext) return { supported: false, mimeType: null, reason: 'insecure' };
    if (!navigator.mediaDevices?.getUserMedia || typeof MediaRecorder === 'undefined') return { supported: false, mimeType: null, reason: 'no_api' };
    const candidates = ['audio/webm;codecs=opus', 'audio/webm', 'audio/mp4', 'audio/ogg;codecs=opus', 'audio/aac'];
    const mimeType = candidates.find((c) => {
      try {
        return MediaRecorder.isTypeSupported(c);
      } catch {
        return false;
      }
    }) ?? '';
    return { supported: true, mimeType: mimeType || null };
  }
}
